import { useState } from "react";
import { toast } from "sonner";
import { useGlobalContext } from "../GlobalContext";

export default function useTeams() {
  const { api, AdminId } = useGlobalContext();

  const [loading, setLoading] = useState(false);

  const shouldShowLocalError = (data) => {
    return (
      data?.responseCode !== "00" &&
      data?.responseCode !== "02" &&
      data?.responseCode !== "03" &&
      data?.responseCode !== "98" &&
      data?.responseCode !== "401"
    );
  };

  // CREATE TEAM - EXISTING FLOW
  // Original API used Basic token, so use api
  const createTeam = async (teamName, accuracy, onSuccess) => {
    if (!teamName || !accuracy) {
      toast.error("Please fill all fields");
      return false;
    }

    setLoading(true);

    try {
      const payload = {
        teamName,
        device: "web",
        userId: "9",
        accuracy,
        adminId: AdminId,
      };

      const res = await api.post("/api/Driver/v1.0/createDriverTeam", payload);

      if (res.data?.responseCode === "00") {
        toast.success(res.data?.responseMessage || "Team created successfully");

        if (typeof onSuccess === "function") {
          await onSuccess();
        }

        return true;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(res.data?.responseMessage || "Unable to create team");
      }

      return false;
    } catch (error) {
      console.error("createTeam error:", error);
      toast.error("Error creating team");
      return false;
    } finally {
      setLoading(false);
    }
  };

  // SEARCH PINCODE DETAILS - USED BY V2 CREATE TEAM FLOW
  const getPincodeDetails = async ({
    word,
    pageSize = 20,
    nextToken = "",
    previousToken = "",
  }) => {
    const cleanWord = String(word || "").trim();

    if (!cleanWord) {
      return null;
    }

    try {
      const payload = {
        word: cleanWord,
        pageSize,
        nextToken,
        previousToken,
      };

      const res = await api.post("/api/Admin/v1.0/getPincodeDetails", payload, {
        headers: {
          accept: "*/*",
          "Content-Type": "application/json-patch+json",
        },
      });

      if (res.data?.responseCode === "00") {
        return res.data;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(res.data?.responseMessage || "Unable to fetch pincodes");
      }

      return null;
    } catch (error) {
      console.error("getPincodeDetails error:", error);
      toast.error(
        error?.response?.data?.responseMessage || "Error fetching pincodes",
      );
      return null;
    }
  };

  // CREATE PINCODE / AREA TEAM - NEW V2 FLOW
  const createDriverTeamV2 = async ({
    pinCode,
    address,
    teamName,
    minimumDrivers,
    maximumDrivers,
    accuracy,
    onSuccess,
  }) => {
    const cleanPinCode = String(pinCode || "").trim();
    const cleanAddress = String(address || "").trim();
    const cleanTeamName = String(teamName || "").trim();
    const cleanMinimumDrivers = String(minimumDrivers ?? "").trim();
    const cleanMaximumDrivers = String(maximumDrivers ?? "").trim();
    const cleanAccuracy = String(accuracy || "").trim();

    if (
      !cleanPinCode ||
      !cleanAddress ||
      !cleanTeamName ||
      !cleanMinimumDrivers ||
      !cleanMaximumDrivers ||
      !cleanAccuracy
    ) {
      toast.error("Please fill all fields");
      return false;
    }

    const minDrivers = Number(cleanMinimumDrivers);
    const maxDrivers = Number(cleanMaximumDrivers);

    if (
      !Number.isFinite(minDrivers) ||
      !Number.isFinite(maxDrivers) ||
      minDrivers < 0 ||
      maxDrivers < 0
    ) {
      toast.error("Minimum and maximum drivers must be valid numbers");
      return false;
    }

    if (minDrivers > maxDrivers) {
      toast.error("Minimum drivers cannot be greater than maximum drivers");
      return false;
    }

    setLoading(true);

    try {
      const payload = {
        adminId: String(AdminId || ""),
        pinCode: cleanPinCode,
        address: cleanAddress,
        teamName: cleanTeamName,
        minimumDrivers: cleanMinimumDrivers,
        maximumDrivers: cleanMaximumDrivers,
        accuracy: cleanAccuracy,
      };

      const res = await api.post("/api/Admin/v2.0/createDriverTeam", payload, {
        headers: {
          accept: "*/*",
          "Content-Type": "application/json-patch+json",
        },
      });

      if (res.data?.responseCode === "00") {
        toast.success(res.data?.responseMessage || "Team created successfully");

        if (typeof onSuccess === "function") {
          await onSuccess();
        }

        return true;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(res.data?.responseMessage || "Unable to create team");
      }

      return false;
    } catch (error) {
      console.error("createDriverTeamV2 error:", error);
      toast.error(
        error?.response?.data?.responseMessage || "Error creating pincode team",
      );
      return false;
    } finally {
      setLoading(false);
    }
  };

  // ASSIGN SHOP TO TEAM
  // Original API used Bearer token, so use bearerApi
  const assignShopToTeam = async ({ shopCode, teamId, onSuccess }) => {
    if (!shopCode || !teamId) {
      toast.error("Shop code and team are required");
      return false;
    }

    setLoading(true);

    try {
      const res = await api.post("/api/Driver/v1.0/assignTeamToShop", {
        shopCode: String(shopCode),
        teamId: String(teamId),
        adminId: String(AdminId),
      });

      if (res.data?.responseCode === "00") {
        toast.success(
          res.data?.responseMessage || "Team assigned successfully",
        );

        if (typeof onSuccess === "function") {
          await onSuccess();
        }

        return true;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(res.data?.responseMessage || "Failed to assign shop");
      }

      return false;
    } catch (error) {
      console.error("assignShopToTeam error:", error);
      toast.error("Error assigning shop");
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    createTeam,
    getPincodeDetails,
    createDriverTeamV2,
    assignShopToTeam,
  };
}
